/** Canonical pure display-number policy. Mirrored byte-for-byte into web/lib/displayFormat.ts;
 * tests/contract/webMirrorContracts.test.ts guards the browser build boundary. */
/** Human-readable size on the 1024 ladder, capped at TB. One decimal below ten units,
 * none for bytes or larger amounts, no grouping, and the viewer's decimal separator.
 * Nonpositive or nonfinite sizes render "0 B". */
export function formatBytes(bytes: number, locale = 'en'): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = Math.min(units.length - 1, Math.max(0, Math.floor(Math.log(bytes) / Math.log(1024))));
  let n = bytes / 1024 ** i;
  if (i < units.length - 1 && Math.round(n) === 1024) {
    i += 1;
    n = bytes / 1024 ** i;
  }
  const digits = n >= 10 || i === 0 ? 0 : 1;
  return new Intl.NumberFormat(locale, { useGrouping: false, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(digits === 0 ? Math.round(n) : Number(n.toFixed(1))) + ` ${units[i]}`;
}

/** Human window label from its minute span, mirroring the CLI rail (300 → "5h", 10080 → "weekly"). */
export function usageWindowLabel(minutes: number | null, weekly: string, windowWord: string): string {
  if (minutes === 10_080) return weekly;
  if (minutes == null || minutes <= 0) return windowWord;
  if (minutes % 1_440 === 0) return `${minutes / 1_440}d`;
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  return `${Math.round(minutes)}m`;
}

/** A USD cost as a fixed "$0.1234" chip: deliberately NOT localized, so a statusline or a KPI keeps its
 *  width whatever the viewer's locale is. The localized rendering — and the rule that an amount nothing
 *  priced is never printed as free — is {@link formatUsd}. `decimals` defaults to 4; the chat statuslines
 *  pass 2 for a coarser chip. */
export function formatCost(usd: number | null, decimals = 4): string {
  return usd === null || !Number.isFinite(usd) ? '—' : `$${usd.toFixed(decimals)}`;
}

/** Shared usage-pressure thresholds for CLI and browser meters: warning at 70%, critical at 90%. */
export function usagePressure(pct: number): 'normal' | 'warning' | 'critical' {
  return pct >= 90 ? 'critical' : pct >= 70 ? 'warning' : 'normal';
}

export interface CacheHitInput {
  cacheRead: number | null | undefined;
  cacheWrite: number | null | undefined;
  input: number | null | undefined;
}

/** Share of prompt tokens served from cache. Cache writes are misses: those tokens were not in cache. */
export function cacheHitPct(u: CacheHitInput | null | undefined): number | null {
  const cacheRead = u?.cacheRead ?? 0;
  const reads = cacheRead + (u?.cacheWrite ?? 0) + (u?.input ?? 0);
  return reads > 0 ? (cacheRead / reads) * 100 : null;
}

/** Render a settled measurement, never an activity estimate. Standard keeps positive sub-unit samples;
 * compact CLI metadata omits rates below one and uses unlocalized whole numbers, including Infinity. */
export function formatSpeed(tps: number | null | undefined, locale?: string, fractionDigits?: 1, mode: 'standard' | 'compact' = 'standard'): string {
  const compact = mode === 'compact';
  if (tps == null) return compact ? '' : '—';
  if (compact ? !(tps >= 1) : !Number.isFinite(tps) || tps <= 0) return compact ? '' : '—';
  const value = compact ? tps : Math.max(0.1, tps);
  const digits = compact ? 0 : fractionDigits ?? (value < 1 ? 1 : 0);
  const label = !compact && locale
    ? new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value)
    : digits ? value.toFixed(digits) : String(Math.round(value));
  return `${!compact && tps < 0.1 ? '<' : ''}${label} ${compact ? 't/s' : 'tok/s'}`;
}

/** Compact count for dense labels; exact localized counts remain a surface formatting choice. */
export function formatCompactCount(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/** Nullable context projection. Rails clamp finite readings to 0–100; raw footer labels retain
 * the reported value, including NaN and Infinity. No reading means no measured fill. */
export function contextMeter(percent: number | null | undefined, mode: 'clamped' | 'raw' = 'clamped'): { percent: number | null; label: string } {
  const value = percent == null ? null : mode === 'raw' ? percent : !Number.isFinite(percent) ? null : Math.max(0, Math.min(100, percent));
  return { percent: value, label: value == null ? '—' : `${Math.round(value)}%` };
}

/** The ONE retention rule for a provider's usage reading, applied by the daemon's `UsageService` and by every
 * client (web, CLI) that keeps its own copy through a failed read. A window's last successful reading stays
 * true until that window's own `resetsAt` (Unix seconds): a failed or empty poll says nothing about it, so
 * only the reset withdraws it, and a reading with no window left is withdrawn whole. A successful read
 * replaces the state, so this is applied to a kept reading, never to a fresher one.
 *
 * A window without `resetsAt` (OpenRouter's balance meters) has no moment at which its figure stops being
 * true, so nothing can keep it past the read that reported it: it shows with a successful read (`failed`
 * false) and is dropped with a failed one. Account selection stays with the caller so a failure cannot reuse
 * another account's data. */
export function providerUsageAfterRead<T extends { windows: readonly { resetsAt: number | null }[] }>(
  usage: T | null | undefined,
  failed: boolean,
  now: number,
): T | null {
  if (!usage) return null;
  const seconds = now / 1000;
  const windows = usage.windows.filter((window) => window.resetsAt === null ? !failed : window.resetsAt > seconds);
  if (!windows.length) return null;
  return windows.length === usage.windows.length ? usage : { ...usage, windows };
}

/** A rail shows live detached processes, not the foreground Bash handle still owned by its tool call.
 * Filtering is presentation-only: the underlying snapshot must retain every foreground handle. */
export function railProcessVisible(process: { running: boolean; completionMode?: string }): boolean {
  return process.running && process.completionMode !== 'foreground';
}